import React, { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { motion } from "framer-motion";

import { ERROR_MESSAGE_SUPPORT } from "../../common/helpersTxt";
import CashierService from "../../api/cashier";
import CashierClosingTicket from "./ticket/CashierClosingTicket";
import CashierLinkTree from "./CashierLinkTree";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import { alpha, useTheme } from "@mui/material/styles";

import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import StorefrontIcon from "@mui/icons-material/Storefront";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { dispatchSnackBarMessage } from "../../utils/snackbar";
import { getDeliveryCenterConfig } from "../../api/DeliveryCenter/deliveryCenter";

import {
  AnimatedModal,
  ModalHeader,
  ModalContent,
} from "../modals/AnimatedModal";
import {
  Panel,
  SectionBar,
  SectionBarLabel,
} from "../styled/panel";
import MoneyTextField from "../Inputs/MoneyTextField";
import { ModernButton } from "../styled/button";

// ── Animation variants ──────────────────────────────────────────────────────
const cardStagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const cardItem = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: "easeOut" },
  },
};

// ── Cashier type definitions ────────────────────────────────────────────────
const CASHIER_TYPES = [
  {
    type: 1,
    label: "Abrir Caixa Geral",
    description: "Todos os canais de venda",
    icon: StorefrontIcon,
    color: "primary",
    configKey: "allowCashierGeneral",
    requiresStartValue: false,
  },
  {
    type: 2,
    label: "Abrir Caixa Delivery",
    description: "Apenas pedidos delivery",
    icon: DeliveryDiningIcon,
    color: "pink",
    configKey: "allowCashierDelivery",
    requiresStartValue: true,
  },
  {
    type: 3,
    label: "Abrir Caixa Salão",
    description: "Apenas pedidos do salão",
    icon: TableRestaurantIcon,
    color: "secondary",
    configKey: "allowCashierIndoor",
    requiresStartValue: true,
  },
];

export default function CreateCashierModal({ show, create }) {
  const theme = useTheme();
  const componentRef = useRef(null);

  const [loadingCashier, setLoadingCashier] = useState(true);
  const [cashier, setCashier] = useState(null);
  const [startValue, setStartValue] = useState("");
  const [reportData, setReportData] = useState(null);

  const [configs, setConfigs] = useState({
    allowCashierGeneral: false,
    allowCashierDelivery: false,
    allowCashierIndoor: false,
  });

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    onPrintError: (error) => {
      console.warn("Print library failed, using fallback", error);
      dispatchSnackBarMessage(
        `print-error-${Date.now()}`,
        `Erro ao imprimir: ${error.message}`,
        "error"
      );
    },
  });

  useEffect(() => {
    const timer = setTimeout(async () => {
      await loadData();
    }, 10);
    return () => clearTimeout(timer);
  }, []);

  const handleOpenCashier = (type) => {
    create({ start_value_cents: parseFloat(startValue) * 100, type });
  };

  const showErrorMessage = () => {
    dispatchSnackBarMessage(
      `cashier-info-${new Date()}`,
      ERROR_MESSAGE_SUPPORT,
      "error"
    );
  };

  const loadData = async () => {
    try {
      setLoadingCashier(true);

      const params = {
        config:
          "allow_cashier_general,allow_cashier_delivery,allow_cashier_indoor",
      };
      const {
        data: {
          allow_cashier_general,
          allow_cashier_delivery,
          allow_cashier_indoor,
        },
      } = await getDeliveryCenterConfig(params);

      setConfigs({
        allowCashierGeneral: allow_cashier_general,
        allowCashierDelivery: allow_cashier_delivery,
        allowCashierIndoor: allow_cashier_indoor,
      });

      const { data } = await CashierService.getLastCashierAnyOperator();
      setCashier(data);

      await loadReportInfos(data);
    } catch (e) {
      if (e.response) {
        showErrorMessage();
      }
    } finally {
      setLoadingCashier(false);
    }
  };

  const loadReportInfos = async (cashier) => {
    try {
      // Use stored report (has correct structure for CashierClosingTicket)
      const { data } = await CashierService.getCashierStoredReport({
        cashier_id: cashier.id,
        id: cashier.id,
      });
      setReportData(data);
    } catch {
      // Fallback to live report if stored report not available
      const { data } = await CashierService.getCashierReport({
        cashier_id: cashier.id,
      });

      // Transform live report format to match CashierClosingTicket expected structure
      setReportData({
        channels: {
          slapiepos: data.slapiepos,
          ifood: data.ifood,
          neemo: data.neemo,
          ddireto: data.ddireto,
          indoor: data.indoor,
        },
        financial_summary: {
          total_orders: data.totals?.orders || 0,
          total_finance: data.totals?.finance || 0,
          money_finance: data.totals?.money_finance || 0,
          online_finance: data.totals?.online_finance || 0,
          card_finance: data.totals?.card_finance || 0,
          start_value: cashier.start_value_cents
            ? cashier.start_value_cents / 100
            : 0,
          end_value: cashier.end_value_cents
            ? cashier.end_value_cents / 100
            : 0,
        },
        payment_methods: [],
        full_report: {
          totals: data.totals,
        },
      });
    }
    setLoadingCashier(false);
  };

  const isTypeDisabled = (cashierType) => {
    if (cashierType.requiresStartValue && !startValue) return true;
    if (!configs[cashierType.configKey]) return true;
    return false;
  };

  return (
    <AnimatedModal
      isOpen={show}
      onClose={() => {}}
      width={cashier ? "85%" : "480px"}
      maxWidth={cashier ? "960px" : "95vw"}
      maxHeight="90vh"
    >
      <ModalHeader
        title="Caixa"
        icon={PointOfSaleIcon}
        rightContent={
          <Chip
            size="small"
            label="Nenhum caixa aberto"
            sx={{
              mr: 1,
              fontWeight: 600,
              fontSize: "0.72rem",
              backgroundColor: alpha(theme.palette.warning.main, 0.1),
              color: theme.palette.warning.dark,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
            }}
          />
        }
      />

      <ModalContent padding={0}>
        <Grid container sx={{ minHeight: 300 }}>
          {/* ── Left Column: Open Cashier ─────────────────────────── */}
          <Grid
            item
            xs={12}
            sm={6}
            md={cashier ? 6 : 12}
            sx={{
              borderRight: cashier
                ? { xs: "none", sm: `1px solid ${theme.palette.divider}` }
                : "none",
              borderBottom: cashier
                ? { xs: `1px solid ${theme.palette.divider}`, sm: "none" }
                : "none",
            }}
          >
            <Box sx={{ p: 3 }}>
              <MoneyTextField
                id="cashier-start-value"
                label="Valor Inicial"
                value={startValue}
                fullWidth
                setValue={(val) => setStartValue(val)}
              />

              <motion.div
                variants={cardStagger}
                initial="hidden"
                animate="visible"
              >
                <Stack spacing={1.5} sx={{ mt: 2.5 }}>
                  {CASHIER_TYPES.map((ct) => {
                    const disabled = isTypeDisabled(ct);
                    const CIcon = ct.icon;

                    return (
                      <motion.div key={ct.type} variants={cardItem}>
                        <CashierTypeButton
                          icon={CIcon}
                          label={ct.label}
                          description={ct.description}
                          color={ct.color}
                          disabled={disabled}
                          onClick={() => handleOpenCashier(ct.type)}
                          allowed={configs[ct.configKey]}
                        />
                      </motion.div>
                    );
                  })}
                </Stack>
              </motion.div>

              <Box sx={{ mt: 2.5 }}>
                <CashierLinkTree />
              </Box>
            </Box>
          </Grid>

          {/* ── Right Column: Last Cashier Report ─────────────────── */}
          {loadingCashier && !cashier && (
            <Grid item xs={12} sm={6} md={6}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  py: 8,
                  gap: 1.5,
                }}
              >
                <CircularProgress size={28} />
                <Typography variant="caption" color="text.secondary">
                  Carregando...
                </Typography>
              </Box>
            </Grid>
          )}

          {cashier && (
            <Grid item xs={12} sm={6} md={6}>
              <Box sx={{ p: 2, height: "100%" }}>
                <SectionBar sx={{ mb: 1.5, borderRadius: "6px 6px 0 0" }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <ReceiptLongIcon
                      sx={{ fontSize: 15, color: "text.secondary" }}
                    />
                    <SectionBarLabel>Último Fechamento</SectionBarLabel>
                  </Stack>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <IconButton
                      size="small"
                      onClick={handlePrint}
                      sx={{
                        width: 26,
                        height: 26,
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.08
                        ),
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.15
                          ),
                        },
                      }}
                    >
                      <LocalPrintshopIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </motion.div>
                </SectionBar>

                {cashier && reportData ? (
                  <Panel
                    sx={{
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                      maxHeight: "55vh",
                      overflowY: "auto",
                    }}
                  >
                    <Box ref={componentRef}>
                      <CashierClosingTicket
                        startDate={cashier.date_time_begin}
                        endDate={cashier.started_finish_at}
                        cashier={cashier}
                        reportData={reportData}
                        backgroundColor={"#fffde7"}
                      />
                    </Box>
                  </Panel>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: 6,
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </ModalContent>
    </AnimatedModal>
  );
}

// ── Cashier Type Button ─────────────────────────────────────────────────────

function CashierTypeButton({
  icon: Icon,
  label,
  description,
  color,
  disabled,
  onClick,
  allowed,
}) {
  const theme = useTheme();

  return (
    <ModernButton
      onClick={onClick}
      disabled={disabled}
      fullWidth
      color={color}
      sx={{
        py: 1.5,
        px: 2,
        justifyContent: "flex-start",
        textAlign: "left",
        borderRadius: 1.5,
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ width: "100%" }}
      >
        <Icon sx={{ fontSize: 20 }} />
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: "inherit", lineHeight: 1.3 }}
          >
            {label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: disabled
                ? theme.palette.text.disabled
                : alpha("#fff", 0.7),
              lineHeight: 1.2,
            }}
          >
            {description}
          </Typography>
        </Box>
        {!allowed && (
          <Chip
            label="Desativado"
            size="small"
            sx={{
              height: 20,
              fontSize: "0.65rem",
              fontWeight: 600,
              backgroundColor: alpha(theme.palette.grey[500], 0.15),
              color: disabled
                ? theme.palette.text.disabled
                : alpha("#fff", 0.6),
            }}
          />
        )}
      </Stack>
    </ModernButton>
  );
}
